// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ChatMessageList.tsx
 * @input Uses React, StyleX, ChatListContext, theme tokens, spacing step utilities
 * @output Exports ChatMessageList component and ChatMessageListProps
 * @position Presentational message container — holds ChatMessage children
 *
 * Renders a container with role="log" for chat message histories.
 * Handles density context, configurable gap, empty state,
 * a spacer that pushes messages to the bottom, and an infinite scroll sentinel.
 *
 * Auto-scroll and the scroll-to-bottom button are owned by
 * ChatLayout. When used standalone (without a layout), the list
 * is purely presentational — compose useChatStreamScroll yourself if needed.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Chat/index.ts (exports)
 * - /apps/storybook/stories/Chat.stories.tsx
 * - /packages/cli/templates/blocks/components/ChatMessageList/ (block examples)
 */
import { useEffect, useMemo, useRef, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars } from "../theme/tokens.stylex.js";
import { ChatListContext, useChatLayoutContext } from "./ChatContext.js";
import { mergeProps } from "../utils/index.js";
import { Spinner } from "../Spinner/index.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kUk6DE: "x98rzlu",
    kAzted: "x2lwn1j",
    $$css: true
  },
  inner: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kUk6DE: "x98rzlu",
    kAzted: "x2lwn1j",
    $$css: true
  },
  gapCompact: {
    kOIVth: "x1txdalj",
    k8WAf4: "xce4md1",
    kg3NbH: "xrrkdod",
    $$css: true
  },
  gapBalanced: {
    kOIVth: "x18g69wz",
    k8WAf4: "x1na6nto",
    kg3NbH: "x1pzlopt",
    $$css: true
  },
  gapSpacious: {
    kOIVth: "x1qh66ti",
    k8WAf4: "xq6koh6",
    kg3NbH: "xm7rs69",
    $$css: true
  }
};
const gapStyles = {
  "0": {
    kOIVth: "xsn7fz1",
    $$css: true
  },
  "1": {
    kOIVth: "xzye2dw",
    $$css: true
  },
  "2": {
    kOIVth: "x1txdalj",
    $$css: true
  },
  "3": {
    kOIVth: "xjcht0a",
    $$css: true
  },
  "4": {
    kOIVth: "x18g69wz",
    $$css: true
  },
  "5": {
    kOIVth: "x9mgr7n",
    $$css: true
  },
  "6": {
    kOIVth: "x1qh66ti",
    $$css: true
  },
  "8": {
    kOIVth: "x4t41sb",
    $$css: true
  },
  "10": {
    kOIVth: "x3hoi3v",
    $$css: true
  },
  "0.5": {
    kOIVth: "x1lsbc85",
    $$css: true
  },
  "1.5": {
    kOIVth: "x1s4dlld",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * Presentational container for chat messages.
 *
 * Renders messages in a flex column with density-based spacing.
 * Override gap to tune row spacing separately from density.
 * A spacer pushes content to the bottom when the list isn't full.
 * Supports loading older messages via `scrollToTopAction`.
 *
 * Auto-scroll and the scroll-to-bottom button are owned by
 * ChatLayout. Use useChatStreamScroll for standalone scroll control.
 *
 * @example
 * ```
 * <ChatMessageList>
 *   <ChatMessage sender="assistant" name="Navi" avatar={<Avatar name="Navi" size="md" />}>
 *     <ChatMessageBubble>Hello!</ChatMessageBubble>
 *   </ChatMessage>
 * </ChatMessageList>
 * ```
 */
export function ChatMessageList({
  children,
  emptyState,
  scrollToTopAction,
  density = 'balanced',
  gap,
  isStreaming = false,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ref,
  ...rest
}) {
  const layoutContext = useChatLayoutContext();
  const sentinelRef = useRef(null);
  const innerRef = useRef(null);
  const [isLoadingTop, startTransition] = useTransition();

  // Register inner content element with the layout for height observation
  useEffect(() => {
    if (layoutContext?.contentRef && innerRef.current) {
      layoutContext.contentRef(innerRef.current);
      return () => layoutContext.contentRef(null);
    }
  }, [layoutContext]);
  const hasChildren = children != null && children !== false && !(Array.isArray(children) && children.length === 0);

  // IntersectionObserver for scroll-to-top infinite scroll
  useEffect(() => {
    const scrollContainer = layoutContext?.scrollContainerRef?.current;
    if (!scrollToTopAction || !sentinelRef.current) {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        startTransition(async () => {
          await scrollToTopAction();
        });
      }
    }, {
      root: scrollContainer ?? null,
      threshold: 0
    });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [scrollToTopAction, layoutContext]);
  const contextValue = useMemo(() => ({
    density
  }), [density]);
  const densityGapStyle = density === 'compact' ? styles.gapCompact : density === 'spacious' ? styles.gapSpacious : styles.gapBalanced;
  const gapOverrideStyle = gap == null ? null : gapStyles[gap];
  return /*#__PURE__*/_jsx(ChatListContext, {
    value: contextValue,
    children: /*#__PURE__*/_jsx("div", {
      ...rest,
      ref: ref,
      role: "log",
      "aria-live": "polite",
      "aria-busy": isStreaming || undefined,
      tabIndex: 0,
      "data-testid": testId,
      ...mergeProps(themeProps('chat-message-list', {
        density
      }), stylex.props(styles.root, xstyle), className, style),
      children: /*#__PURE__*/_jsxs("div", {
        ref: innerRef,
        ...stylex.props(styles.inner, densityGapStyle, gapOverrideStyle),
        children: [scrollToTopAction && /*#__PURE__*/_jsx("div", {
          ref: sentinelRef,
          "aria-hidden": true
        }), isLoadingTop && /*#__PURE__*/_jsx("div", {
          ...{
            className: "x78zum5 xl56j7k x8o8v82"
          },
          children: /*#__PURE__*/_jsx(Spinner, {
            size: "md"
          })
        }), /*#__PURE__*/_jsx("div", {
          ...{
            className: "x98rzlu x2lwn1j"
          },
          "aria-hidden": true
        }), hasChildren ? children : emptyState ? /*#__PURE__*/_jsx("div", {
          ...{
            className: "x78zum5 x6s0dn4 xl56j7k x98rzlu x2lwn1j"
          },
          children: emptyState
        }) : null]
      })
    })
  });
}
ChatMessageList.displayName = 'ChatMessageList';